import { shoppingApi } from '../api/shopping';

// ADR 014 — offline-first queue. Actions taken while offline are stored in
// IndexedDB and replayed when the connection returns. Scope (Fase 2 MVP):
// checklist status updates only.

export interface OfflinePickUpdate {
  sessionId: string;
  itemId: string;
  pickStatus: 'TO_PICK' | 'PICKED' | 'SKIPPED' | 'NOT_FOUND';
  unitPrice?: string | null;
}

interface QueueEntry {
  id: string;
  payload: OfflinePickUpdate;
  createdAt: number;
  retries: number;
}

const DB_NAME = 'homestock-offline';
const STORE = 'queue';
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB indisponível'));
  });
}

async function put(entry: QueueEntry): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(entry);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error ?? new Error('Falha ao enfileirar')); };
  });
}

async function getAll(): Promise<QueueEntry[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => { db.close(); resolve(req.result ?? []); };
    req.onerror = () => { db.close(); reject(req.error ?? new Error('Falha ao ler fila')); };
  });
}

async function remove(id: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error ?? new Error('Falha ao remover da fila')); };
  });
}

/** Store an action to replay when back online. Safe to call at any time. */
export async function enqueuePickUpdate(payload: OfflinePickUpdate): Promise<void> {
  await put({
    id: `${payload.sessionId}:${payload.itemId}:${payload.pickStatus}`,
    payload,
    createdAt: Date.now(),
    retries: 0,
  });
}

export function offlineQueueSize(): Promise<number> {
  return getAll().then((entries) => entries.length);
}

let flushing = false;

/**
 * Replay queued actions. Failed entries stay in the queue for the next
 * attempt (last-write-wins per field, ADR 014).
 */
export async function flushOfflineQueue(): Promise<number> {
  if (flushing) return 0;
  flushing = true;
  try {
    const entries = await getAll();
    let synced = 0;
    for (const entry of entries) {
      try {
        const { sessionId, itemId, pickStatus, unitPrice } = entry.payload;
        await shoppingApi.updatePickStatus(sessionId, itemId, {
          pickStatus,
          unitPrice: unitPrice ?? null,
        });
        await remove(entry.id);
        synced++;
      } catch {
        // keep entry for next flush
      }
    }
    return synced;
  } finally {
    flushing = false;
  }
}
